import { useTranslation } from "react-i18next";
import { useFormik } from "formik";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CreateProviderRequestDTO,
  useCreateProviderMutation,
  useToast,
} from "@/hooks";
import { ProviderValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { TaxIdInput } from "@/components/ui";

type FormCreateProvider = {
  handleClose: () => void;
};

export function FormCreateProvider({ handleClose }: FormCreateProvider) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const initialValues = {
    name: "",
    cpf: "",
    cnpj: "",
    phone: "",
    address: "",
    comments: "",
    name_of_person_responsible: "",
    phone_person_responsible: "",
  };

  const { mutateAsync, isLoading } = useCreateProviderMutation();

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: ProviderValidationSchema(),
    onSubmit: async (values) => {
      handleCreateProviderSubmit(values);
    },
  });

  async function handleCreateProviderSubmit(request: CreateProviderRequestDTO) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          handleClose();
          formik.resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2"
    >
      <ScrollArea className="h-full">
        <div className="space-y-4 pr-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label required>{t("LABEL.name")}</Label>
              <Input
                isInvalid={formik.touched.name && formik.errors.name}
                isValid={formik.touched.name && !formik.errors.name}
                {...formik.getFieldProps("name")}
                messageError={formik.errors.name}
              />
            </div>
            <div className="space-y-1">
              <Label
                required={
                  (!formik.values.cpf && !formik.values.cnpj) ||
                  !!formik.values.cpf
                }
              >
                {t("LABEL.cpf")}
              </Label>
              <TaxIdInput
                typeMask="cpf"
                isInvalid={formik.touched.cpf && formik.errors.cpf}
                isValid={formik.touched.cpf && !formik.errors.cpf}
                {...formik.getFieldProps("cpf")}
                messageError={formik.errors.cpf}
              />
            </div>
            <div className="space-y-1">
              <Label
                required={
                  (!formik.values.cpf && !formik.values.cnpj) ||
                  !!formik.values.cnpj
                }
              >
                {t("LABEL.cnpj")}
              </Label>
              <TaxIdInput
                typeMask="cnpj"
                isInvalid={formik.touched.cnpj && formik.errors.cnpj}
                isValid={formik.touched.cnpj && !formik.errors.cnpj}
                {...formik.getFieldProps("cnpj")}
                messageError={formik.errors.cnpj}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.phone")}</Label>
              <Input
                inputPhone
                isInvalid={formik.touched.phone && formik.errors.phone}
                isValid={formik.touched.phone && !formik.errors.phone}
                {...formik.getFieldProps("phone")}
                messageError={formik.errors.phone}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.contact person")}</Label>
              <Input
                isInvalid={
                  formik.touched.name_of_person_responsible &&
                  formik.errors.name_of_person_responsible
                }
                isValid={
                  formik.touched.name_of_person_responsible &&
                  !formik.errors.name_of_person_responsible
                }
                {...formik.getFieldProps("name_of_person_responsible")}
                messageError={formik.errors.name_of_person_responsible}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.contact phone")}</Label>
              <Input
                inputPhone
                isInvalid={
                  formik.touched.phone_person_responsible &&
                  formik.errors.phone_person_responsible
                }
                isValid={
                  formik.touched.phone_person_responsible &&
                  !formik.errors.phone_person_responsible
                }
                {...formik.getFieldProps("phone_person_responsible")}
                messageError={formik.errors.phone_person_responsible}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label required>{t("LABEL.address")}</Label>
            <Input
              isInvalid={formik.touched.address && formik.errors.address}
              isValid={formik.touched.address && !formik.errors.address}
              {...formik.getFieldProps("address")}
              messageError={formik.errors.address}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("LABEL.description")}</Label>
            <Textarea
              isInvalid={formik.touched.comments && formik.errors.comments}
              isValid={formik.touched.comments && !formik.errors.comments}
              {...formik.getFieldProps("comments")}
              messageError={formik.errors.comments}
            />
          </div>
        </div>
      </ScrollArea>
      <div>
        <Button isLoading={isLoading} disabled={isLoading} className="px-10">
          {t("LABEL.save")}
        </Button>
      </div>
    </form>
  );
}
